import { useCallback, useEffect, useState } from "react";
import {
  fetchClassLists,
  fetchClassGroups,
  saveClassAssignmentChanges,
} from "../api/classesApi";
import {
  dedupeStudents,
  enrichStudentsWithGroups,
  validateChangedRows,
} from "../utils/classListHelpers";

// "3 students moved, 1 assigned, 2 unassigned." — what the backend actually
// did, from its { moved, assigned, unassigned, total } response.
export function describeSaveResult(result) {
  const n = (k) => Number(result?.[k]) || 0;
  const parts = [];
  if (n("moved")) parts.push(`${n("moved")} moved`);
  if (n("assigned")) parts.push(`${n("assigned")} assigned`);
  if (n("unassigned")) parts.push(`${n("unassigned")} unassigned`);
  if (parts.length === 0) return "Changes saved.";
  const total = n("total") || parts.reduce((a, p) => a + parseInt(p, 10), 0);
  return `${total} student${total === 1 ? "" : "s"} saved: ${parts.join(", ")}.`;
}

export default function useClassesData({
  period,
  selectedCampus,
  showToast,
}) {
  const [classlists, setClasslists] = useState([]);
  const [classes, setClasses] = useState([]);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);

  const refreshData = useCallback(async () => {
    if (!period || !selectedCampus) {
      setClasslists([]);
      setClasses([]);
      return;
    }

    setLoading(true);
    setClasslists([]);

    const [studentResult, groupResult] = await Promise.allSettled([
      fetchClassLists(period, selectedCampus),
      fetchClassGroups(selectedCampus, period.startDate, period.endDate),
    ]);

    const studentData =
      studentResult.status === "fulfilled" && Array.isArray(studentResult.value)
        ? studentResult.value
        : [];
    const groupData =
      groupResult.status === "fulfilled" && Array.isArray(groupResult.value)
        ? groupResult.value
        : [];

    if (studentResult.status === "rejected") {
      console.error("Failed to fetch class lists", studentResult.reason);
    }
    if (groupResult.status === "rejected") {
      console.error("Failed to fetch class groups", groupResult.reason);
    }

    if (
      studentResult.status === "rejected" &&
      groupResult.status === "rejected"
    ) {
      showToast?.("Failed to load classes", "error");
    }

    const enriched = enrichStudentsWithGroups(studentData, groupData);
    const deduped = dedupeStudents(enriched);

    setClasses(groupData);
    setClasslists(deduped);
    setLoading(false);
  }, [period, selectedCampus, showToast]);

  useEffect(() => {
    setChanges([]);
    refreshData();
  }, [refreshData]);

  const handleClassChange = useCallback(
    (studentId, field, value) => {
      if (!selectedCampus) {
        showToast?.(
          "Please select a campus before assigning students.",
          "warning"
        );
        return;
      }

      setClasslists((prev) =>
        prev.map((student) => {
          if (student.studentId !== studentId) return student;

          // Where the period's class_lists load said this student is. Sent to the
          // backend as the save's precondition (fromClassId) and checked there
          // against the same class_lists — so it's the loaded value, not
          // filtered through our idea of which groups are classes. The feed
          // serialises "in no class" as "0", so normalise to a number or null.
          const originalClassGroupId = Number(student.group_id) || null;

          const updated = { ...student };

          let matchedGroup = null;

          if (field === "group_name") {
            updated.group_name = value;

            matchedGroup =
              classes.find(
                (group) =>
                  String(group.groupName || "").trim().toLowerCase() ===
                  String(value || "").trim().toLowerCase()
              ) || null;

            if (matchedGroup) {
              updated.group_id = matchedGroup.id;
              updated.group_name = matchedGroup.groupName;
              updated.adult_full_name = matchedGroup.leaderName || "";
            } else {
              updated.group_id = null;
              updated.adult_full_name = "";
            }
          } else if (field === "adult_full_name") {
            updated.adult_full_name = value;

            const teacherGroups = classes.filter(
              (group) => String(group.leaderName || "") === String(value || "")
            );

            const currentMatch =
              teacherGroups.find(
                (group) =>
                  String(group.groupName || "").trim().toLowerCase() ===
                  String(student.group_name || "").trim().toLowerCase()
              ) || null;

            matchedGroup = currentMatch || teacherGroups[0] || null;

            if (matchedGroup) {
              updated.group_id = matchedGroup.id;
              updated.group_name = matchedGroup.groupName;
              updated.adult_full_name = matchedGroup.leaderName || "";
            } else {
              updated.group_id = null;
              updated.group_name = "";
            }
          } else {
            updated[field] = value;
          }

          const nextChange = {
            studentId,
            previousGroup: originalClassGroupId,
            newGroup: matchedGroup ? matchedGroup.id : null,
          };

          setChanges((prevChanges) => {
            const existingIndex = prevChanges.findIndex(
              (change) => change.studentId === studentId
            );

            const existingChange =
              existingIndex > -1 ? prevChanges[existingIndex] : null;

            const mergedChange = existingChange
              ? {
                  ...existingChange,
                  newGroup: nextChange.newGroup,
                }
              : nextChange;

            const noEffectiveChange =
              mergedChange.previousGroup === mergedChange.newGroup ||
              (!mergedChange.previousGroup && !mergedChange.newGroup);

            if (noEffectiveChange) {
              return prevChanges.filter(
                (change) => change.studentId !== studentId
              );
            }

            if (existingIndex > -1) {
              const copy = [...prevChanges];
              copy[existingIndex] = mergedChange;
              return copy;
            }

            return [...prevChanges, mergedChange];
          });

          return updated;
        })
      );
    },
    [classes, selectedCampus, showToast]
  );

  const handleSaveChanges = useCallback(
    async ({ visibleRows, refreshData }) => {
      if (changes.length === 0) {
        showToast?.("No changes to save.", "info");
        return;
      }

      const invalids = validateChangedRows({
        visibleRows,
        changes,
        classes,
      });

      if (invalids.length > 0) {
        showToast?.(
          `Cannot save: invalid class name(s) for ${invalids.length} student(s).`,
          "error"
        );
        return;
      }

      try {
        const result = await saveClassAssignmentChanges(
          changes,
          period,
          selectedCampus
        );
        setChanges([]);
        showToast?.(describeSaveResult(result), "success");
        await refreshData();
      } catch (err) {
        console.error("Failed to save changes:", err);
        if (err?.code === "STALE_CLASS_ASSIGNMENT") {
          // Someone else changed this period's classes since we loaded them
          // and nothing was applied. Reload so the table shows what's actually
          // there — our edits were made against a state that no longer exists.
          setChanges([]);
          await refreshData();
          showToast?.(err.message, "warning");
          return;
        }
        showToast?.(err?.message || "Error saving changes.", "error");
      }
    },
    [changes, classes, period, selectedCampus, showToast]
  );

  return {
    classlists,
    classes,
    changes,
    loading,
    handleClassChange,
    handleSaveChanges,
    refreshData,
  };
}